import { DocumentWithSelection, HTMLElementWithDocument } from '@routes';

import { HighlightedElement, IEHTMLTextAreaElement, HTMLWrappedElement, WrapperTagNames } from './ModuleModal.types';

import {
  IFRAME_BOTTOM_OFFSET,
  MODULE_CONTENT_DIV_ID,
  MODULE_ELEMENT_ID_PREFIX,
  MODULE_INTERACTION_ACTIVE_ID,
  MODULE_INTERACTION_HOVER_ID,
  MODULE_INTERACTION_ACTIVE_ATTRIBUTE,
  MODULE_INTERACTION_DRAG_ATTRIBUTE,
  MODULE_INTERACTION_HOVER_ATTRIBUTE,
  ORIGINAL_ID_ATTRIBUTE,
  MODULE_INTERACTION_ACTIVE_HEIGHT,
} from './ModuleModal.const';

/**
 * ModuleModal component utils
 */

export const pasteTextIntoTextarea = (elem: HTMLInputElement, text: string) => {
  elem.focus();

  if (typeof elem.selectionStart == 'number' && typeof elem.selectionEnd == 'number') {
    const val = elem.value;
    const selStart = elem.selectionStart;
    elem.value = val.slice(0, selStart) + text + val.slice(elem.selectionEnd);
    elem.selectionEnd = elem.selectionStart = selStart + text.length;
  } else if (typeof (document as DocumentWithSelection).selection != 'undefined') {
    const textRange = (document as DocumentWithSelection).selection.createRange();
    textRange.text = text;
    textRange.collapse(false);
    textRange.select();
  }
};

export const setCaretPosition = (ctrl: IEHTMLTextAreaElement, pos: number | null) => {
  // Modern browsers
  if (ctrl.setSelectionRange) {
    ctrl.focus();
    ctrl.setSelectionRange(pos, pos);

    // IE8 and below
  } else if (ctrl.createTextRange) {
    const range = ctrl.createTextRange();
    range.collapse(true);
    range.moveEnd('character', pos);
    range.moveStart('character', pos);
    range.select();
  }
};

export class Cursor {
  static getCurrentCursorPosition(parentElement: any) {
    const doc = parentElement.ownerDocument;
    const win = doc.defaultView;
    const selection = win.getSelection();
    let charCount = -1,
      node;

    if (selection?.focusNode) {
      if (Cursor._isChildOf(selection.focusNode, parentElement)) {
        node = selection.focusNode;
        charCount = selection.focusOffset;

        // if (selection.focusNode.parentElement.tagName === 'PERSONEFY-TOKEN') {
        //   charCount += selection.focusNode.parentElement.outerHTML.length + 1;
        //   Cursor.setCurrentCursorPosition(charCount, parentElement);
        //   return charCount;
        // }

        while (node) {
          if (node === parentElement) {
            break;
          }

          if (node.previousSibling) {
            node = node.previousSibling;
            if (node.tagName === 'PERSONEFY-TOKEN' || node.tagName === 'SUP') {
              charCount += node.outerHTML.length;
            } else {
              charCount += node.textContent.length;
            }
          } else {
            node = node.parentNode;
            if (node === null) {
              break;
            }
          }
        }
      }
    }

    return charCount;
  }

  static setCurrentCursorPosition(chars: number, element: any) {
    if (chars >= 0) {
      const doc = element.ownerDocument;
      const win = doc.defaultView;
      const selection = win.getSelection();

      const range = Cursor._createRange(element, { count: chars });

      if (range) {
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }

  static _createRange(node: any, chars: { count: number }, range: Range | undefined = undefined): Range {
    if (!range) {
      const doc = node.ownerDocument;
      range = doc.createRange() as Range;
      range.selectNode(node);
      range.setStart(node, 0);
    }

    if (chars.count === 0) {
      range.setEnd(node, chars.count);
    } else if (node && chars.count > 0) {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.length < chars.count) {
          chars.count -= node.textContent.length;
        } else {
          range.setEnd(node, chars.count);
          chars.count = 0;
        }
      } else {
        for (let lp = 0; lp < node.childNodes.length; lp++) {
          range = Cursor._createRange(node.childNodes[lp], chars, range);

          if (chars.count === 0) {
            break;
          }
        }
      }
    }

    return range;
  }

  static _isChildOf(node: any, parentElement: any) {
    while (node !== null) {
      if (node === parentElement) {
        return true;
      }
      node = node.parentNode;
    }

    return false;
  }
}

export const getHighlightedElems = (e: MouseEvent, elemId: string, prevState: HighlightedElement[]) => {
  const elements = [...prevState];

  elements.unshift({
    id: elemId,
    event: e,
  });

  if (elements.length > 2) {
    elements.length = 2;
  }

  return elements;
};

// add two values and return the result
export const addWidths = (widths: string[], unit = 'px'): string => {
  const result = widths.reduce((acc, cur) => {
    return acc + parseInt(cur, 10);
  }, 0);
  return `${result}${unit}`;
};

const resetIframeOverlay = (overlay: HTMLElement) => {
  overlay.style.display = 'none';
};

export const removeIframeOverlays = (iframe: HTMLIFrameElement) => {
  // remove overlays from iframe
  const frameDoc = iframe.contentDocument;
  const frameBody = frameDoc?.body;

  if (frameBody) {
    const overlays = frameDoc?.querySelectorAll<HTMLElement>(
      `#${MODULE_INTERACTION_HOVER_ID}, #${MODULE_INTERACTION_ACTIVE_ID}`,
    );

    if (overlays) {
      for (const overlay of overlays) {
        overlay.remove();
      }
    }
  }
};

// attach iframe element hover and active overlays
export const attachIframeOverlays = (iframe: HTMLIFrameElement) => {
  // attach overlays to iframe if they do not exist
  const frameDoc = iframe.contentDocument;
  const frameBody = frameDoc?.body;

  if (frameBody) {
    removeIframeOverlays(iframe);

    // Attach the hover and active state overlays
    const hoverDiv = document.createElement('div');
    hoverDiv.id = MODULE_INTERACTION_HOVER_ID;
    frameBody.append(hoverDiv);

    const activeDiv = document.createElement('div');
    activeDiv.id = MODULE_INTERACTION_ACTIVE_ID;
    frameBody.append(activeDiv);
  }
};

export const positionIframeOverlay = (overlay: HTMLElement, target: HTMLElement) => {
  const rect = target.getBoundingClientRect();

  overlay.style.position = 'absolute';
  overlay.style.top = `${rect.top + window.scrollY}px`;
  overlay.style.left = `${rect.left + window.scrollX}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
  // overlay.style.display = 'flex';
  overlay.style.display = 'block';
};

export const repositionIframeOverlay = () => {};

const clearAllActiveElements = (contentDocument: Document) => {
  const activeElements = contentDocument.querySelectorAll(
    `[${MODULE_INTERACTION_ACTIVE_ATTRIBUTE}="true"], [${MODULE_INTERACTION_HOVER_ATTRIBUTE}="true"]`,
  );

  for (const element of activeElements) {
    element.removeAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE);
    element.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
  }

  const activeOverlay = contentDocument.querySelector(`#${MODULE_INTERACTION_ACTIVE_ID}`) as HTMLElement;
  if (activeOverlay) {
    resetIframeOverlay(activeOverlay);
  }
};

// Element mouse enter, leave and click event handlers
export const elemMouseEnter = (e: MouseEvent, elemId: string, documentElemId: string, isTemplateView: boolean) => {
  //redefine elem styles
  e.stopPropagation();
  e.preventDefault();

  const currentTarget = e.currentTarget as HTMLElement;

  if (!currentTarget) {
    return;
  }

  const { contentDocument } = document.getElementById(documentElemId) as HTMLIFrameElement;

  // if the target has an active attribute, do not show the hover state
  if (currentTarget.hasAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE)) {
    // reset the active state position
    const activeOverlay = contentDocument?.querySelector(`#${MODULE_INTERACTION_ACTIVE_ID}`) as HTMLElement;

    if (currentTarget && activeOverlay) {
      positionIframeOverlay(activeOverlay, currentTarget);
    }

    return;
  }

  currentTarget.setAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE, 'true');

  // set the hover state position
  const hoverOverlay = contentDocument?.querySelector(`#${MODULE_INTERACTION_HOVER_ID}`) as HTMLElement;

  if (currentTarget && hoverOverlay) {
    positionIframeOverlay(hoverOverlay, currentTarget);
  }
};

export const elemMouseLeave = (e: MouseEvent) => {
  //redefine elem styles
  if (e) {
    e.stopPropagation();
  }

  const target = e?.target as HTMLElement;

  // hide the hover overlay
  const ownerDocument = target?.ownerDocument;
  const hoverOverlay = ownerDocument.querySelector(`#${MODULE_INTERACTION_HOVER_ID}`) as HTMLElement;

  if (hoverOverlay) {
    resetIframeOverlay(hoverOverlay);
  }

  target?.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
};

export const elemClick = async (
  e: MouseEvent,
  elemId: string,
  documentElemId: string,
  // setChosenElemId: (value: SetStateAction<string>) => void,
) => {
  e.stopPropagation();
  e.preventDefault();

  const { contentDocument } = document.getElementById(documentElemId) as HTMLIFrameElement;

  if (contentDocument) {
    // clear all elements with active and hovered attributes
    clearAllActiveElements(contentDocument);

    const currentTarget = e.currentTarget as HTMLElement;

    // set the active state overlay
    const activeOverlay = contentDocument.querySelector(`#${MODULE_INTERACTION_ACTIVE_ID}`) as HTMLElement;
    if (currentTarget && activeOverlay) {
      currentTarget.setAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE, 'true');
      positionIframeOverlay(activeOverlay, currentTarget);
    }

    // remove the hover state overlay
    const hoverOverlay = contentDocument.querySelector(`#${MODULE_INTERACTION_HOVER_ID}`) as HTMLElement;
    if (hoverOverlay) {
      resetIframeOverlay(hoverOverlay);
    }
  }
};

const setOriginalId = (elem: HTMLElement) => {
  const initialId = elem.getAttribute('id');
  const originalId = elem.getAttribute(ORIGINAL_ID_ATTRIBUTE);

  if (initialId && !initialId.startsWith(MODULE_ELEMENT_ID_PREFIX) && !originalId) {
    elem.setAttribute(ORIGINAL_ID_ATTRIBUTE, initialId);
  }
};

export const resizeModalIframe = (iframe: HTMLIFrameElement, isTemplateEditor = true) => {
  const iframeBody = iframe.contentDocument?.body;

  if (iframeBody) {
    // update the iframe height based on the content
    const iframeResizeHeight = iframeBody.getBoundingClientRect().height + MODULE_INTERACTION_ACTIVE_HEIGHT;
    iframe.style.height = `${iframeResizeHeight}px`;

    // update the module content div height based on the iframe content
    //const moduleContentDiv = document.getElementById('module-content') as HTMLDivElement
    // For now, we will use the iframe's parent element
    const parentElement = iframe.parentNode as HTMLElement;
    if (parentElement) {
      const iframeBottomOffset = isTemplateEditor ? IFRAME_BOTTOM_OFFSET : 0;

      const visibleAreaHeight = window.innerHeight - parentElement.getBoundingClientRect().top - iframeBottomOffset;

      if (visibleAreaHeight < iframeBody.getBoundingClientRect().height) {
        parentElement.style.height = `${visibleAreaHeight ?? 0}px`;
        parentElement.style.overflowY = 'scroll';
      } else {
        parentElement.style.height = '';
        parentElement.style.overflowY = 'visible';
      }
    }
  }
};

export const initializeLoadingImages = (
  iframe: HTMLIFrameElement,
  contentDocument: Document,
  isTemplateEditor = true,
) => {
  const images = Array.from(contentDocument.querySelectorAll('img'));

  images.forEach((img) => {
    if (img.complete) {
      // Image is already loaded, resize just in case
      resizeModalIframe(iframe, isTemplateEditor);
    } else {
      img.addEventListener('load', () => {
        resizeModalIframe(iframe, isTemplateEditor);
      });
    }
  });
};

export const initializeElemIds = (contentDocument: Document, tagName: WrapperTagNames) => {
  const elems = contentDocument.querySelectorAll(tagName);

  Array.from(elems).forEach((elem, index) => {
    // const currentTargetAttributes = elem?.attributes as HTMLWrappedElement;
    // const textContent = currentTargetAttributes.style?.textContent;

    setOriginalId(elem);

    elem.setAttribute('id', `${MODULE_ELEMENT_ID_PREFIX}${tagName}-${index}`);
    elem.setAttribute('originalInnerHTML', elem.innerHTML.trim());
  });
};

export const initializeLinkIds = (contentDocument: Document) => {
  const links = contentDocument.querySelectorAll('a');
  // let buttonCount = 0;

  Array.from(links).forEach((link, index) => {
    setOriginalId(link);

    const linkAttributes = link?.attributes as HTMLWrappedElement;
    // const textContent = linkAttributes.style?.textContent;
    // const hasBackgroundColor = Array.from(new Set(textContent?.split(';'))).some((item) =>
    //   item.includes('background-color'),
    // );

    // if (hasBackgroundColor) {
    //   link.setAttribute('id', `${MODULE_ELEMENT_ID_PREFIX}button-${index}`);
    //   buttonCount += 1;
    // } else {
    //   link.setAttribute('id', `${MODULE_ELEMENT_ID_PREFIX}a-${index - buttonCount}`);
    // }
    link.setAttribute('id', `${MODULE_ELEMENT_ID_PREFIX}a-${index}`);
  });
};

export const initializeDivIds = (contentDocument: Document) => {
  const textDivs = Array.from(contentDocument.querySelectorAll('div'));

  textDivs.forEach((textDiv, index) => {
    // check that the ID is not one for the overlays
    const id = textDiv.getAttribute('id');

    if (id !== MODULE_INTERACTION_ACTIVE_ID && id !== MODULE_INTERACTION_HOVER_ID) {
      setOriginalId(textDiv);

      const linkAttributes = textDiv?.attributes as HTMLWrappedElement;

      textDiv.setAttribute('id', `${MODULE_ELEMENT_ID_PREFIX}div-${index}`);
      textDiv.setAttribute(MODULE_INTERACTION_DRAG_ATTRIBUTE, 'true');
    }
  });
};
